import AnnouncementOutlinedIcon from "@mui/icons-material/AnnouncementOutlined";
import { Box, Stack } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { useAtomValue, useSetAtom } from "jotai";
import { noop } from "lodash";
import { useCallback, useEffect } from "react";
import { baseAtom, markChannelMessagesAsRead, storeMessageInChannel } from "../../stores/jotai/base";
import { discoverySessionAtom } from "../../stores/jotai/discovery-session";
import { protectedApiCallCallback } from "../../api/network";
import { ChatMessageView } from "./message";

const MaterialChatHistory = (props) => {
	const {
		channel,
		chatHistory,
		app,
		user,
		unreadCount,
		onScrollRequest,
		isLastMessageForUser,
	} = props;
	const { userID, name: userName } = user;
	const { key: channelID } = channel;
	const discoverySession = useAtomValue(discoverySessionAtom);
	const setBase = useSetAtom(baseAtom);
    const theme = useTheme();
	const getSortedHistory = () => {
		const channelChatHistory = chatHistory[channelID] || [];

		channelChatHistory.sort((message1, message2) => {
			const message1Date = new Date(message1.created);
			const message2Date = new Date(message2.created);

			if (message1Date < message2Date) {
				return -1;
			} else if (message1Date > message2Date) {
				return 1;
			}

			return 0;
		});

		return channelChatHistory;
	};

	const sortedHistory = getSortedHistory();

	const emptyMessageScreen = useCallback(
		(msg) => (
			<Stack
				sx={{
					padding: "40px",
					justifyContent: "center",
					alignItems: "center",
					height: "100%",
				}}
			>
				<AnnouncementOutlinedIcon
					sx={{
						fontSize: "100px",
						color: theme?.custom?.disabledColor,
					}}
				/>
				<Box
					sx={{
						marginTop: "10px",
					}}
				>
					{msg}
				</Box>
			</Stack>
		),
		[]
	);

	useEffect(() => {
		if (sortedHistory?.length > 0 && unreadCount > 0) {
			protectedApiCallCallback(
				discoverySession,
				"seen",
				{
					type: "chat",
					targets: sortedHistory?.map((chat) => chat?.chatID),
				},
				{},
				() => {
					markChannelMessagesAsRead(setBase, channelID);
				},
				"put"
			);
		}
	}, [channelID, unreadCount, discoverySession, sortedHistory]);

	useEffect(() => {
		onScrollRequest();
	}, [sortedHistory?.length]);

	if (!channel?.key) {
		return emptyMessageScreen("You don't have any channels available to chat.");
	}

    return sortedHistory?.length > 0
		? sortedHistory?.map((message,index) => (
					<ChatMessageView
					    key={`${message?.id}-${index}`}
						onScrollRequest={onScrollRequest}
						app={app}
						discoverySession={discoverySession}
						name={userName}
						isLastMessageForUser={isLastMessageForUser(
							message?.id,
							sortedHistory
						)}
						sentBySelf={
							message?.userID === userID || message?.userID === undefined
						}
						onEdit={(newMessageText) => {
							storeMessageInChannel(setBase, message?.channelID, {
								...message,
								message: newMessageText,
								messageEdited: true,
							});
						}}
						onDelete={() => {
							storeMessageInChannel(
                                setBase,
                                message?.channelID,
                                {
									...message,
									deleted: true,
								}
                            );
						}}
						{...message}
						theme={theme}
					/>
			))
		: emptyMessageScreen("You have no messages in this channel.");
};

MaterialChatHistory.defaultProps = {
	channel: {},
	chatHistory: [],
	unreadCount: 0,
	onScrollRequest: noop,
};

export default MaterialChatHistory;
